import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SvgIcon from "../assest/svg/svg.jsx";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";

const ProfileSettings = ({ dir }) => {

  const token = localStorage.getItem("token");
  const base_url = process.env.REACT_APP_PORTAL_APP_BASE_URL;

  const [profileData, setProfileData] = useState({
    name: "",
    employeeId: "",
    email: "",
    company: null,
    department: "",
    initial: "N",
    avatar: "",
    doj: "",
  });
  const [formData, setFormData] = useState({
    oldPassword: "",
    password: "",
    confirmPassword: "",
  });

  const [showPasswords, setShowPasswords] = useState({
    oldPassword: false,
    password: false,
    confirmPassword: false,
  });

  const [loading, setLoading] = useState(false);
  // const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate()
  const { t } = useTranslation();
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value.replace(/\s{2,}/g, " "),
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const oldPassword = formData.oldPassword.trim();
    const password = formData.password.trim();
    const confirmPassword = formData.confirmPassword.trim();
    if (!oldPassword || !password || !confirmPassword) {
      setError(t("Fields are required"));
      return;
    }
    else if (password !== confirmPassword) {
      setError(t("password_does_not_match"));
      return;
    }
    else if (oldPassword === password) {
      setError(t("new_pass_different"));
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_PORTAL_APP_BASE_URL}/auth/tokenPassword`,
        {
          oldPassword: oldPassword,
          password: password,
          confirmPassword: confirmPassword,
        },
        {
          headers: {
            token: `${localStorage.getItem("token")}`,
          },
        }
      );
      setFormData({
        oldPassword: "",
        password: "",
        confirmPassword: "",
      });
      // setShowModal(true);
      setTimeout(() => {
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        sessionStorage.removeItem("conversation");
        sessionStorage.removeItem("currentSessionId");
        sessionStorage.removeItem("sessionNumber");
        sessionStorage.removeItem("sessions");
        navigate("/login");
      }, 1000);
    } catch (err) {
      setError(err?.response?.data?.message || "Error resetting password.");
    } finally {
      setLoading(false);
    }
  };

  const getProfileData = useCallback(async () => {
    try {
      const response = await axios.get(`${base_url}/users/profile`, { headers: { Authorization: `${token}` } });
      const profileData = response.data.data;
      setProfileData((prev) => ({
        ...prev,
        name: profileData.name || "N/A",
        employeeId: profileData.employeeId || "N/A",
        email: profileData.email || "N/A",
        company: profileData.company || null,
        department: profileData.department || "",
        initial: profileData.name ? profileData.name.charAt(0) : "N",
        avatar: profileData.avatar || "",
        doj: profileData.doj || "",
      }));
    } catch (error) {
      console.log(error);
    }
  }, [base_url, token])

  // Add this useEffect for sidebar state management
  useEffect(() => {
    const updateSidebarState = () => {
      const sidebarState = getComputedStyle(document.documentElement).getPropertyValue('--sidebar-open');
      setSidebarOpen(sidebarState === '1');
    };

    updateSidebarState();

    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    const observer = new MutationObserver(updateSidebarState);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Calculate responsive padding based on sidebar state and screen size
  const getContentPadding = () => {
    if (isMobile) {
      return "";
    }
    return sidebarOpen
      ? (dir === 'rtl' ? 'pr-64' : 'pl-64')
      : (dir === 'rtl' ? 'pr-20' : 'pl-20');
  };

  useEffect(() => {
    getProfileData();
  }, [getProfileData]);

  console.log(error);

  return (
    <div className={`m-4 transition-all duration-300 ${getContentPadding()}`} dir={dir}>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => {
            navigate("/")
            sessionStorage.removeItem("conversation");
            sessionStorage.removeItem("currentSessionId");
            sessionStorage.removeItem("sessionNumber");
          }}
          style={{ backgroundColor: "#767676" }}
          className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center transition-all duration-300 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-xl text-white" />
        </button>
        <h2 className="text-xl font-medium text-gray-900">
          {t("My Profile")}
        </h2>
      </div>
      <div className="md:flex pb-28 items-center bg-gradient-to-r from-[#B03982] to-[#733C86] mb-4 p-3 text-white rounded-t-xl">
      </div>
      <div className="mx-auto p-4">

        <div className='flex items-center gap-4'>
          <div style={{ width: '80px', height: '80px' }} className='bg-gray-100 object-cover rounded-full border-4 border-[#B03982] overflow-hidden'>
            <div className="w-full h-full flex items-center justify-center text-black text-[18px] font-semibold">
              {profileData?.name
                ?.split(" ")
                .map(word => word[0])
                .slice(0, 2)
                .join("")
                .toUpperCase()}
            </div>
          </div>
          <div className='flex flex-col gap-1'>
            <p className="text-black text-[18px] font-semibold">{profileData.name}</p>
            <p className="text-gray-700 text-[16px] font-medium">{profileData.email}</p>
          </div>
        </div>

        <form className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mt-10">

          <div className="col-span-1">
            <label htmlFor="name" className="mb-2 text-gray-700 font-medium block">{t("Employee Id")} <span className="text-red-500">*</span></label>
            <input id="name" type="text" value={profileData?.employeeId} disabled={true} className="w-full h-12 px-4 border-2 border-[#B03982] rounded-lg focus:outline-none focus:ring-0 focus:ring-[#B03982]" />
          </div>

          <div className="col-span-1">
            <label htmlFor="name" className="mb-2 text-gray-700 font-medium block">{t("Name")} <span className="text-red-500">*</span></label>
            <input id="name" type="text" value={profileData?.name} disabled={true} className="w-full h-12 px-4 border-2 border-[#B03982] rounded-lg focus:outline-none focus:ring-0 focus:ring-[#B03982]" />
          </div>

          <div className="col-span-1">
            <label htmlFor="name" className="mb-2 text-gray-700 font-medium block">{t("Email")} <span className="text-red-500">*</span></label>
            <input id="name" type="text" value={profileData?.email} disabled={true} className="w-full h-12 px-4 border-2 border-[#B03982] rounded-lg focus:outline-none focus:ring-0 focus:ring-[#B03982]" />
          </div>

          <div className="col-span-1">
            <label htmlFor="doj" className="mb-2 text-gray-700 font-medium block">{t("Company")}</label>
            <input id="doj" type="text" value={profileData?.company?.companyName} disabled={true} className="w-full h-12 px-4 border-2 border-[#B03982] rounded-lg focus:outline-none focus:ring-0 focus:ring-[#B03982]" />
          </div>

          <div className="col-span-1">
            <label htmlFor="doj" className="mb-2 text-gray-700 font-medium block">{t("Department")}</label>
            <input id="doj" type="text" value={profileData?.department?.departmentName} disabled={true} className="w-full h-12 px-4 border-2 border-[#B03982] rounded-lg focus:outline-none focus:ring-0 focus:ring-[#B03982]" />
          </div>

          <div className="col-span-1 md:col-span-2 xl:col-span-3 gap-2">
            <h3 className="text-lg font-bold text-gray-900">
              <span className="inline-block mr-3 w-1.5 h-6 rounded-full bg-[#B03982]" />{t("My email address")}
            </h3>
            <div className='flex items-center gap-4 mt-6'>
              <div className='w-12 h-12 rounded-full bg-[#B03982] flex justify-center items-center'>
                <SvgIcon name="email" size={25} />
              </div>
              <p className="text-black text-[16px] font-semibold">{profileData?.email || profileData.email}</p>
            </div>
          </div>

        </form>

        <h3 className="text-lg font-bold text-gray-900 mt-10 mb-5">
          <span className="inline-block mr-3 w-1.5 h-6 rounded-full bg-[#B03982]" />{t("Change Password")}
        </h3>

        <form className="grid grid-cols-1 md:grid-cols-3 gap-6">

          <div className="relative">
            <label htmlFor="name" className="mb-2 text-gray-700 font-medium block">{t("Old password")} <span className="text-red-500">*</span></label>
            <input
              dir={dir}
              type={showPasswords.oldPassword ? "text" : "password"}
              name="oldPassword"
              value={formData.oldPassword}
              onChange={handleChange}
              placeholder={t("current_password")}
              className="w-full h-12 px-4 border-2 border-[#B03982] rounded-lg focus:outline-none focus:ring-0 focus:ring-[#B03982]"
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility("oldPassword")}
              className={`absolute ${dir === "rtl" ? 'left-3' : 'right-3'} top-1/2 translate-y-1/2 text-gray-500 hover:text-[#B03982]`}
            >
              {showPasswords.oldPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>

          <div className="relative">
            <label htmlFor="name" className="mb-2 text-gray-700 font-medium block">{t("Password")} <span className="text-red-500">*</span></label>
            <input
              dir={dir}
              type={showPasswords.password ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={t("new_password")}
              className="w-full h-12 px-4 border-2 border-[#B03982] rounded-lg focus:outline-none focus:ring-0 focus:ring-[#B03982]"
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility("password")}
              className={`absolute ${dir === "rtl" ? 'left-3' : 'right-3'} top-1/2 translate-y-1/2 text-gray-500 hover:text-[#B03982]`}
            >
              {showPasswords.password ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>

          <div className="relative">
            <label htmlFor="name" className="mb-2 text-gray-700 font-medium block">{t("Confirm Password")} <span className="text-red-500">*</span></label>
            <input
              dir={dir}
              type={showPasswords.confirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder={t("confirm_new_password")}
              className="w-full h-12 px-4 border-2 border-[#B03982] rounded-lg focus:outline-none focus:ring-0 focus:ring-[#B03982]"
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility("confirmPassword")}
              className={`absolute ${dir === "rtl" ? 'left-3' : 'right-3'} top-1/2 translate-y-1/2 text-gray-500 hover:text-[#B03982]`}
            >
              {showPasswords.confirmPassword ? (
                <Eye className="w-5 h-5" />
              ) : (
                <EyeOff className="w-5 h-5" />
              )}
            </button>
          </div>

        </form>

        {error && <div className="text-red-500 mt-4">{error}</div>}

        <div className="flex mt-10">
          <button
            disabled={loading}
            onClick={handleSubmit}
            className={`
                px-6 py-3 rounded-xl font-medium text-white
                bg-gradient-to-r from-[#A73884] to-[#803A85]
                hover:shadow-lg hover:shadow-purple-300/50 transition-all duration-300
                flex items-center gap-2
                w-full sm:w-auto
            `}
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>
              </>
            ) : t("Update password")}
          </button>
        </div>

      </div>
    </div>
  );
};

export default ProfileSettings;